/**
 * Налоги — расчёт, утверждение, авто-перенос в ДДС (expense kind=taxes).
 *
 * Flow:
 *   1. «Рассчитать» → preview: какие налоги доначислятся
 *   2. «Создать черновики» → INSERT draft tax_returns
 *   3. «Утвердить» по строке → создаётся связанный expense, переходит в P&L
 *   4. «Разлочить» (если ошибка) — soft-delete expense + статус draft
 *   5. «Отметить уплаченным» → status=paid + paid_at
 *
 * Расчёт:
 *   • УСН-Д 6%  → поквартально, база = bank_transfer выручка (cash)
 *   • Страховые → помесячно, 30% от sales-ФОТ (МОП/КЦ) brutto
 *   • НДФЛ      → помесячно, 13% от sales-ФОТ brutto
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { Icon } from "@/components/prototype/Icon";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import {
  fetchTaxes, previewTaxes, recomputeTaxes,
  approveTax, unlockTax, patchTax, deleteTax,
  formatRub,
  TAX_KIND_LABELS, TAX_STATUS_LABELS,
  type TaxKind, type TaxStatus, type TaxReturnRow, type TaxPreviewItem,
} from "@/api/finance";
import { ApiError } from "@/api/client";


const KIND_TONE: Record<TaxKind, { bg: string; fg: string; border: string }> = {
  usn:   { bg: "var(--brick-50)",   fg: "var(--brick-deep)", border: "var(--brick-100)" },
  ndfl:  { bg: "var(--green-soft)", fg: "#1F4F1F",           border: "rgba(61,122,61,0.3)" },
  strah: { bg: "var(--amber-soft)", fg: "#7A4E12",           border: "rgba(201,122,31,0.3)" },
  other: { bg: "var(--paper-2)",    fg: "var(--mute)",       border: "var(--line)" },
};

const STATUS_TONE: Record<TaxStatus, { bg: string; fg: string; border: string }> = {
  draft:     { bg: "var(--paper-2)",    fg: "var(--mute)",      border: "var(--line)" },
  approved:  { bg: "rgba(212,167,58,0.15)", fg: "#8a6e10",       border: "rgba(212,167,58,0.4)" },
  paid:      { bg: "var(--green-soft)", fg: "#1F4F1F",          border: "rgba(61,122,61,0.3)" },
  cancelled: { bg: "var(--paper-2)",    fg: "var(--mute-2)",    border: "var(--line)" },
};


function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (isNaN(t)) return null;
  return Math.floor((t - Date.now()) / 86400000);
}


function KindPill({ kind, label }: { kind: TaxKind; label?: string }) {
  const tone = KIND_TONE[kind];
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 999,
      background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {label || TAX_KIND_LABELS[kind]}
    </span>
  );
}

function StatusPill({ status }: { status: TaxStatus }) {
  const tone = STATUS_TONE[status];
  return (
    <span style={{
      display: "inline-block", padding: "2px 9px", borderRadius: 999,
      background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
      fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>
      {TAX_STATUS_LABELS[status]}
    </span>
  );
}


function PreviewBlock({ year, onCreated, onClose }: {
  year: number; onCreated: () => void; onClose: () => void;
}) {
  const qc = useQueryClient();
  const previewQ = useQuery({
    queryKey: ["tax-preview", year],
    queryFn: () => previewTaxes({ year }),
  });
  const recomputeMut = useMutation({
    mutationFn: () => recomputeTaxes({ year }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["taxes"] });
      onCreated();
    },
  });

  if (previewQ.isLoading) return <Spinner size="md" label="Считаю налоги…" />;
  if (previewQ.error) {
    const msg = previewQ.error instanceof ApiError ? `HTTP ${previewQ.error.status}` : (previewQ.error as Error).message;
    return <div className="alert alert-red"><div className="alert-body">{msg}</div></div>;
  }

  const data = previewQ.data!;
  if (data.count === 0) {
    return (
      <div className="card" style={{ background: "var(--green-soft)", borderColor: "rgba(61,122,61,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, color: "#1F4F1F" }}>За {year} год новых начислений нет</div>
            <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4 }}>
              Все периоды с данными уже имеют черновики или утверждённые записи. Чтобы пересчитать —
              сначала удали или разлочь существующие.
            </div>
          </div>
          <button className="chip" onClick={onClose}>Закрыть</button>
        </div>
      </div>
    );
  }

  // Группировка по period_label — отдельная карточка на месяц/квартал/год
  const byPeriod = new Map<string, TaxPreviewItem[]>();
  for (const it of data.items) {
    if (!byPeriod.has(it.period_label)) byPeriod.set(it.period_label, []);
    byPeriod.get(it.period_label)!.push(it);
  }
  // Сортировка периодов по period_from
  const periods = Array.from(byPeriod.entries())
    .sort((a, b) => {
      const af = a[1][0]?.period_from || "";
      const bf = b[1][0]?.period_from || "";
      return af.localeCompare(bf);
    });

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <div>
          <h3 className="card-title">Предварительный расчёт за {year}</h3>
          <div className="card-sub">
            {data.count} {data.count === 1 ? "черновик" : "черновиков"} на сумму <b>{formatRub(data.total_amount, { unit: true })}</b>
            {" — по "}{periods.length} {periods.length === 1 ? "периоду" : "периодам"}.
          </div>
        </div>
        <div className="card-actions">
          <button className="chip" onClick={onClose}>Отмена</button>
          <button
            className="chip active"
            style={{ background: "var(--brick)", color: "#fff", borderColor: "var(--brick)" }}
            disabled={recomputeMut.isPending}
            onClick={() => recomputeMut.mutate()}
          >
            {recomputeMut.isPending ? "Создаю…" : `Создать ${data.count} черновиков`}
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))", gap: 12 }}>
        {periods.map(([periodLabel, items]) => (
          <PeriodCard key={periodLabel} periodLabel={periodLabel} items={items} />
        ))}
      </div>
    </div>
  );
}


function fmtPeriodTitle(periodLabel: string, periodKind: TaxPreviewItem["period_kind"]): string {
  // 2026-04 → "Апрель 2026", 2026-Q2 → "II квартал 2026"
  if (periodKind === "quarter") {
    const m = /^(\d{4})-Q([1-4])$/.exec(periodLabel);
    if (m) {
      const roman = ["I", "II", "III", "IV"][parseInt(m[2], 10) - 1];
      return `${roman} квартал ${m[1]}`;
    }
  }
  if (periodKind === "month") {
    const m = /^(\d{4})-(\d{2})$/.exec(periodLabel);
    if (m) {
      const months = ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"];
      const mi = parseInt(m[2], 10) - 1;
      return `${months[mi]} ${m[1]}`;
    }
  }
  return periodLabel;
}


function PeriodCard({ periodLabel, items }: { periodLabel: string; items: TaxPreviewItem[] }) {
  // Все items в одном period_label должны иметь одинаковый period_kind
  const periodKind = items[0]?.period_kind || "month";
  const totalCalc = items.reduce((a, i) => a + i.calc_amount, 0);
  const isQuarter = periodKind === "quarter";

  return (
    <div style={{
      background: isQuarter ? "var(--brick-50)" : "var(--card)",
      border: `1px solid ${isQuarter ? "var(--brick-100)" : "var(--line)"}`,
      borderRadius: 10, padding: "14px 16px",
      display: "flex", flexDirection: "column", gap: 12,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
        <div>
          <div style={{
            fontFamily: "var(--serif)", fontWeight: 700, fontSize: 16,
            color: isQuarter ? "var(--brick-deep)" : "var(--ink)",
            letterSpacing: "-0.01em",
          }}>
            {fmtPeriodTitle(periodLabel, periodKind)}
          </div>
          <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>
            {isQuarter ? "УСН — квартальный" : "Месячные налоги"} · {items.length} {items.length === 1 ? "запись" : "записей"}
            {isQuarter && <> · <span style={{ color: "var(--brick-deep)", fontWeight: 600 }}>только безнал</span></>}
          </div>
        </div>
        <div style={{
          fontFamily: "var(--serif)", fontWeight: 700, fontSize: 18,
          color: isQuarter ? "var(--brick)" : "var(--ink)",
        }}>
          {formatRub(totalCalc, { unit: true })}
        </div>
      </div>

      {items.map(it => <TaxItemDetail key={`${it.kind}-${it.period_label}`} item={it} />)}
    </div>
  );
}


function TaxItemDetail({ item }: { item: TaxPreviewItem }) {
  const [open, setOpen] = useState(false);
  const tone = KIND_TONE[item.kind];
  const breakdown = item.base_breakdown || [];
  const hasBreakdown = breakdown.length > 0;

  return (
    <div style={{
      borderTop: "1px dashed var(--line-soft)",
      paddingTop: 10,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <KindPill kind={item.kind} />
          <span style={{ fontSize: 11, color: "var(--mute)" }}>
            срок: {fmtDate(item.due_date)}
          </span>
        </div>
        <div style={{ fontFamily: "var(--serif)", fontWeight: 700, fontSize: 15, color: tone.fg }}>
          {formatRub(item.calc_amount, { unit: true })}
        </div>
      </div>

      <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 6, lineHeight: 1.4 }}>
        <span style={{ color: "var(--mute)" }}>База:</span>{" "}
        <span style={{ fontWeight: 600 }}>{formatRub(item.base_amount, { unit: true })}</span>
        {item.kind === "usn" && (
          <span style={{ color: "var(--mute)", fontSize: 11 }}> (безнал, без нала и карты)</span>
        )}
        {" × "}
        <span style={{ fontWeight: 600 }}>{(item.rate * 100).toFixed(0)}%</span>
        {hasBreakdown && (
          <>
            {" · "}
            <button
              type="button"
              onClick={() => setOpen(v => !v)}
              style={{
                background: "none", border: "none", padding: 0,
                color: "var(--brick)", cursor: "pointer", font: "inherit",
                fontSize: 12, textDecoration: "underline",
              }}
            >
              {open ? "Скрыть детали" : `Показать ${breakdown.length} ${breakdown.length === 1 ? "запись" : "источников"}`}
            </button>
          </>
        )}
      </div>

      {open && hasBreakdown && (
        <div style={{
          marginTop: 8, padding: 8, background: "var(--paper-2)",
          borderRadius: 6, fontSize: 12, color: "var(--ink-3)",
          maxHeight: 240, overflowY: "auto",
        }}>
          {breakdown.map(b => (
            <div key={b.id} style={{
              display: "flex", justifyContent: "space-between", gap: 8,
              padding: "3px 0", borderBottom: "1px dotted var(--line-soft)",
            }}>
              <span style={{ color: "var(--mute)", minWidth: 76, whiteSpace: "nowrap" }}>
                {fmtDate(b.date)}
              </span>
              <span style={{
                flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }} title={b.note || ""}>
                {b.label}
                {b.category && <span style={{ color: "var(--mute-2)" }}> · {b.category}</span>}
              </span>
              <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                {formatRub(b.amount, { unit: true })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function TaxRow({ r, qc }: { r: TaxReturnRow; qc: ReturnType<typeof useQueryClient> }) {
  const approveMut = useMutation({
    mutationFn: () => approveTax(r.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["taxes"] }),
  });
  const unlockMut = useMutation({
    mutationFn: () => unlockTax(r.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["taxes"] }),
  });
  const deleteMut = useMutation({
    mutationFn: () => deleteTax(r.id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["taxes"] }),
  });
  const markPaidMut = useMutation({
    mutationFn: (paid_at: string) => patchTax(r.id, { status: "paid", paid_at }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["taxes"] }),
  });
  const editAmountMut = useMutation({
    mutationFn: (amount: number) => patchTax(r.id, { amount }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["taxes"] }),
  });

  const due = daysUntil(r.due_date);
  const overdue = due !== null && due < 0 && r.status !== "paid" && r.status !== "cancelled";
  const soon = due !== null && due >= 0 && due <= 14 && r.status === "draft";

  return (
    <tr className="ar-row" style={{ borderBottom: "1px solid var(--line-soft)" }}>
      <td><KindPill kind={r.kind} label={r.kind_label} /></td>
      <td style={{ fontWeight: 600 }}>{r.period_label}</td>
      <td style={{ textAlign: "right", color: "var(--ink-3)" }}>
        {formatRub(r.base_amount, { unit: true })}
      </td>
      <td style={{ textAlign: "right", color: "var(--mute)" }}>
        {(r.rate * 100).toFixed(0)}%
      </td>
      <td style={{ textAlign: "right" }}>
        <div style={{
          fontFamily: "var(--serif)", fontWeight: 700, fontSize: 15,
          color: r.status === "paid" ? "var(--green)" : "var(--ink)",
          letterSpacing: "-0.01em",
        }}>
          {formatRub(r.amount, { unit: true })}
        </div>
        {Math.abs(r.amount - r.calc_amount) > 0.5 && (
          <div style={{ fontSize: 10, color: "var(--mute)" }}>
            расчёт: {formatRub(r.calc_amount, { unit: true })}
          </div>
        )}
      </td>
      <td style={{ whiteSpace: "nowrap" }}>
        <span style={{ color: overdue ? "var(--brick)" : (soon ? "var(--amber)" : "var(--ink-3)") }}>
          {fmtDate(r.due_date)}
        </span>
        {due !== null && r.status !== "paid" && r.status !== "cancelled" && (
          <div style={{ fontSize: 11, color: overdue ? "var(--brick)" : (soon ? "var(--amber)" : "var(--mute)") }}>
            {overdue ? `просрочен ${-due} дн` : (due === 0 ? "сегодня" : `через ${due} дн`)}
          </div>
        )}
      </td>
      <td><StatusPill status={r.status} /></td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap", fontSize: 12 }}>
        {r.status === "draft" && (
          <>
            <button
              className="chip" style={{ marginRight: 4 }}
              onClick={() => {
                const v = window.prompt(`Сумма налога к утверждению (расчёт: ${r.calc_amount.toFixed(2)}):`, String(r.amount));
                if (v === null) return;
                const num = parseFloat(v.replace(",", "."));
                if (!isNaN(num) && num >= 0) editAmountMut.mutate(num);
              }}
              title="Изменить сумму"
            >✎</button>
            <button
              className="chip" style={{ marginRight: 4, background: "var(--green-soft)", color: "var(--green)", borderColor: "rgba(61,122,61,0.3)" }}
              disabled={approveMut.isPending}
              onClick={() => {
                if (window.confirm(`Утвердить ${r.kind_label} за ${r.period_label} на ${formatRub(r.amount, { unit: true })}?\nБудет создан расход в ДДС.`)) {
                  approveMut.mutate();
                }
              }}
            ><span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="check" size={11}/>Утвердить</span></button>
            <button
              className="chip" style={{ color: "var(--brick)", borderColor: "var(--brick-100)" }}
              disabled={deleteMut.isPending}
              onClick={() => {
                if (window.confirm(`Удалить запись "${r.kind_label} за ${r.period_label}"?`)) {
                  deleteMut.mutate();
                }
              }}
            ><Icon name="x" size={11}/></button>
          </>
        )}
        {r.status === "approved" && (
          <>
            <button
              className="chip" style={{ marginRight: 4, background: "var(--green-soft)", color: "var(--green)", borderColor: "rgba(61,122,61,0.3)" }}
              disabled={markPaidMut.isPending}
              onClick={() => {
                const today = new Date().toISOString().slice(0, 10);
                const v = window.prompt("Дата уплаты (ГГГГ-ММ-ДД):", today);
                if (v) markPaidMut.mutate(v);
              }}
            >💸 Уплачено</button>
            <button
              className="chip" style={{ marginRight: 4 }}
              disabled={unlockMut.isPending}
              onClick={() => {
                if (window.confirm("Разлочить запись?\nСвязанный расход в ДДС будет удалён (soft-delete), запись вернётся в черновики.")) {
                  unlockMut.mutate();
                }
              }}
            ><span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><Icon name="lock" size={11}/>Разлочить</span></button>
            {r.expense_id && (
              <span style={{ fontSize: 11, color: "var(--mute)" }}>expense #{r.expense_id}</span>
            )}
          </>
        )}
        {r.status === "paid" && (
          <span style={{ fontSize: 11, color: "var(--green)" }}>
            {r.paid_at && fmtDate(r.paid_at)}
          </span>
        )}
      </td>
    </tr>
  );
}


export function FinanceTaxesPage() {
  const qc = useQueryClient();
  const today = new Date();
  const [year, setYear] = useState<number>(today.getFullYear());
  const [showPreview, setShowPreview] = useState(false);
  const [kindFilter, setKindFilter] = useState<"" | TaxKind>("");

  const q = useQuery({
    queryKey: ["taxes", year],
    queryFn: () => fetchTaxes({ year }),
  });

  const items = useMemo(() => {
    const all = q.data?.items || [];
    if (!kindFilter) return all;
    return all.filter(i => i.kind === kindFilter);
  }, [q.data, kindFilter]);

  if (q.isLoading) return (
    <FinanceLayout>
      <Spinner size="lg" label="Загрузка налогов…" />
    </FinanceLayout>
  );
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return (
        <FinanceLayout>
          <div className="alert alert-amber"><div className="alert-body">Доступ только для админа/руководителя</div></div>
        </FinanceLayout>
      );
    }
    const msg = err instanceof ApiError ? `HTTP ${err.status}` : (err as Error).message;
    return (
      <FinanceLayout>
        <div className="alert alert-red"><div className="alert-body">{msg}</div></div>
      </FinanceLayout>
    );
  }

  const data = q.data!;
  const allItems = data.items;
  const totalDraft = data.totals.draft;
  const totalApproved = data.totals.approved;
  const totalPaid = data.totals.paid;

  // К уплате в ближайшие 14 дней (approved + draft, не paid)
  const dueSoon = allItems
    .filter(i => (i.status === "approved" || i.status === "draft") && i.due_date)
    .filter(i => {
      const d = daysUntil(i.due_date);
      return d !== null && d <= 14;
    });
  const dueSoonSum = dueSoon.reduce((a, i) => a + i.amount, 0);

  const years: number[] = [];
  for (let y = today.getFullYear() + 1; y >= today.getFullYear() - 3; y--) years.push(y);

  return (
    <FinanceLayout>
      {/* HERO */}
      <div className="hero-overview" style={{ marginBottom: 20 }}>
        <div>
          <div className="hero-headline">
            {formatRub(totalDraft + totalApproved, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
            Всего начислено налогов за {year} год по {allItems.length} {allItems.length === 1 ? "записи" : "записям"}.<br />
            Из них уплачено {formatRub(totalPaid, { unit: true })}.
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">К утверждению</div>
          <div className="hero-stat-value" style={{ color: totalDraft > 0 ? "#FFD23F" : "#fff" }}>
            {formatRub(totalDraft, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            {allItems.filter(i => i.status === "draft").length} черновиков
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">К уплате ≤ 14 дн</div>
          <div className="hero-stat-value" style={{ color: dueSoonSum > 0 ? "#FF6B6B" : "#fff" }}>
            {formatRub(dueSoonSum, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            {dueSoon.length} {dueSoon.length === 1 ? "запись" : "записей"} к платежу
          </div>
        </div>
      </div>

      {/* TOOLBAR */}
      <div className="card card-tight" style={{ marginBottom: 16, padding: "12px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
          <div className="chip-row" style={{ marginBottom: 0 }}>
            <span style={{ fontSize: 12, color: "var(--mute)", marginRight: 4, alignSelf: "center" }}>Год:</span>
            {years.map(y => (
              <button
                key={y}
                className={`chip ${year === y ? "active" : ""}`}
                onClick={() => { setYear(y); setShowPreview(false); }}
              >
                {y}
              </button>
            ))}
          </div>
          <div className="chip-row" style={{ marginBottom: 0, marginLeft: 12 }}>
            <span style={{ fontSize: 12, color: "var(--mute)", marginRight: 4, alignSelf: "center" }}>Налог:</span>
            <button
              className={`chip ${kindFilter === "" ? "active" : ""}`}
              onClick={() => setKindFilter("")}
            >Все</button>
            {(["usn", "strah", "ndfl"] as TaxKind[]).map(k => (
              <button
                key={k}
                className={`chip ${kindFilter === k ? "active" : ""}`}
                onClick={() => setKindFilter(kindFilter === k ? "" : k)}
              >
                {TAX_KIND_LABELS[k]}
              </button>
            ))}
          </div>
          <span style={{ flex: 1 }} />
          <button
            className="chip active"
            style={{ background: "var(--brick)", color: "#fff", borderColor: "var(--brick)" }}
            onClick={() => setShowPreview(v => !v)}
          >
            {showPreview ? "Скрыть расчёт" : "🔄 Рассчитать налоги"}
          </button>
        </div>
      </div>

      {/* PREVIEW */}
      {showPreview && (
        <PreviewBlock
          year={year}
          onCreated={() => setShowPreview(false)}
          onClose={() => setShowPreview(false)}
        />
      )}

      {/* TABLE */}
      {items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--mute)", padding: 32 }}>
          За {year} год записей нет.{" "}
          <button
            onClick={() => setShowPreview(true)}
            style={{
              background: "none", border: "none", padding: 0, margin: 0,
              color: "var(--brick)", cursor: "pointer", font: "inherit", textDecoration: "underline",
            }}
          >Рассчитать?</button>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, color: "var(--ink-2)" }}>
            <thead>
              <tr style={{
                background: "var(--paper-2)", color: "var(--mute)", fontSize: 11,
                textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500,
              }}>
                <th style={{ textAlign: "left", padding: "10px 14px" }}>Налог</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>Период</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>База</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Ставка</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Сумма</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>Срок</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>Статус</th>
                <th style={{ textAlign: "right", padding: "10px 14px" }}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => <TaxRow key={r.id} r={r} qc={qc} />)}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .ar-row > td { padding: 12px 8px; transition: background 100ms ease; }
        .ar-row:hover > td { background: var(--paper-2); }
      `}</style>
    </FinanceLayout>
  );
}
